import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, distinctUntilChanged, ReplaySubject, Subscription, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { AppState, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { closeRtcPeer } from 'app/utils/media/rtc';
import AudioTrack from 'entity/AudioTrack';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import {
  IncomingMessageType,
  IncomingMessage,
  MusicOfferMessage,
  OutgoingMessageType,
  ReconnectMusicPeerMessage,
} from 'messages/websocket.messages';
import SocialController from 'rest/SocialController';

import MessageType from '../message/message-type';
import ParticipantRtcStats from '../preciate-stream/participant-rtc-stats';
import SoundControl from '../sound-control';
import { socialAppActions, socialAppSelectors } from '../state/app';

import { SceneService } from './scene.service';
import { SocialMessageService } from './social-message.service';
import { VideoRoomService } from './video-room.service';
import { WebsocketService } from './websocket.service';

export const NONE_PLAYLIST_ID = '00000000-0000-0000-0000-000000000000';
export const NONE_PLAYLIST: Playlist = {
  playlistId: NONE_PLAYLIST_ID,
  name: 'None',
  type: 0,
};
export const NO_TRACK: AudioTrack = { title: '', artists: '', album: '' };
const STATS_INTERVAL = 1000;

@Injectable({
  providedIn: 'root',
})
export class PlaylistService extends Loggable {
  public playlistId: string = NONE_PLAYLIST_ID;
  private currentPlaylist = NONE_PLAYLIST;
  private _currentTrack: AudioTrack = NO_TRACK;

  private playlistChanged = new BehaviorSubject<Playlist>(this.currentPlaylist);
  public playlistChanged$ = this.playlistChanged.asObservable();
  public peerConnection?: RTCPeerConnection;

  // Track peer connection start time (unix milliseconds)
  private peerConnectionTime?: number;
  private rtcStats?: ParticipantRtcStats;
  private rtcVideoStatsInterval?: Timeout;

  public social?: Social;
  private userId?: string;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private messageSubscription?: Subscription;
  private soundControl = SoundControl.getInstance(this.sceneService);

  public constructor(
    private messageService: SocialMessageService,
    private websocketService: WebsocketService,
    private videoRoomService: VideoRoomService,
    private store: Store<AppState>,
    private sceneService: SceneService,
  ) {
    super();
    this.setDisplayName('PlaylistService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);
    this.messageSubscription = this.websocketService.message$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.handleWebsocketMessage);

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(
        distinctUntilChanged((a, b) => a?.playlistId === b?.playlistId),
        takeUntil(this.destroyed$),
      )
      .subscribe((social) => {
        this.social = social;
        if (!social) {
          return;
        }
        const { playlistId } = social;
        void this.setPlaylist(playlistId ?? NONE_PLAYLIST_ID).catch((e) => this.error('Failed to set playlist', e));
      });

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));
  }

  private handleWebsocketMessage = async (msg: IncomingMessage): Promise<void> => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.MUSINF: {
        const { playing, track } = msg;
        // Set Track Info
        if (playing && track) {
          this._currentTrack = track;
          return;
        }
        // Set None Playlist
        await this.setPlaylist(NONE_PLAYLIST_ID).catch((e) => this.error('Error setting playlist', e));
        return;
      }

      case IncomingMessageType.OFFER: {
        await this.handleMusicOffer(msg).catch((e) => this.error('Error handling music offer', e));
        return;
      }
    }
  };

  // Handle Playlist Management
  public get currentTrack(): AudioTrack {
    return this._currentTrack;
  }

  public async nextTrack(): Promise<void> {
    if (!this.social) {
      this.error('nextTrack social is undefined');
      return;
    }
    const { socialId } = this.social;
    assertDefined(socialId);

    this._currentTrack = await new SocialController().nextAudioTrack(socialId);
  }

  public async changeRoomPlaylist(playlistId: string): Promise<void> {
    if (!this.social) {
      this.error('changeRoomPlaylist social is undefined');
      return;
    }
    const { socialId } = this.social;
    assertDefined(socialId);
    const socialController = new SocialController();

    if (playlistId !== NONE_PLAYLIST.playlistId) {
      await socialController.playAudioTrack(socialId, playlistId).catch((e) => this.error('Error play audio track', e));
    } else {
      await socialController.stopPlaying(socialId).catch((e) => this.error('Error stop playing audio track', e));
    }

    this.store.dispatch(socialAppActions.CHANGE_PLAYLIST({ playlistId }));

    // Send Message
    this.messageService.sendMessage({
      type: MessageType.PLAYLIST_CHANGED,
      identity: playlistId,
    });
  }

  private async setPlaylist(playlistId: string): Promise<void> {
    this.info('Set Playlist to', playlistId);
    this.playlistId = playlistId;

    try {
      // Real playlist
      if (playlistId !== NONE_PLAYLIST_ID) {
        const resultPlaylist = await new SocialController().getPlaylist(playlistId);
        this.currentPlaylist = resultPlaylist ?? NONE_PLAYLIST;

        // None Playlist
      } else {
        this.currentPlaylist = NONE_PLAYLIST;
      }

      this.playlistChanged.next(this.currentPlaylist);
    } catch (e) {
      this.error(`Failed to get new playlist for ${this.userId}`, e);
    }
  }

  // Handle Music Peer Connection
  private handleConnectionFailed(): void {
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
    }

    const msg: ReconnectMusicPeerMessage = { t: OutgoingMessageType.RECONNECT_MUSIC_PEER };
    this.websocketService.sendMessage(msg);
  }

  private async handleMusicOffer(offerMsg: MusicOfferMessage): Promise<void> {
    this.info('handleMusicOffer', offerMsg);
    this.peerConnection?.close();
    const cfg = offerMsg.rtcConfig;
    const rtcConfig = cfg && JSON.parse(cfg);
    const { rtcConfig: _rtcConfig, ...offer } = offerMsg;

    const config = this.videoRoomService.createRtcConfig(rtcConfig);
    const peerConnection: RTCPeerConnection = new RTCPeerConnection(config);
    this.peerConnection = peerConnection;

    peerConnection.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = peerConnection;

      this.info('Subscribe ICE state change', { iceConnectionState, peerTime: this.peerConnectionTime });
    };

    peerConnection.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = peerConnection;
      this.info('Subscribe ICE gathering state change', { iceGatheringState, peerTime: this.peerConnectionTime });
    };

    peerConnection.onicecandidate = (event) => {
      const { candidate } = event;
      if (!candidate) {
        this.info('ICE Candidate was null, done');
        return;
      }

      this.info('ICE Candidate received', candidate.toJSON());

      this.websocketService.sendMessage(candidate, { force: true });
      return;
    };

    peerConnection.onicecandidateerror = (e) => this.trace('Candidate error', e);

    peerConnection.ontrack = async (event) => {
      this.info('Subscribe ontrack', event);
      if (event.track.kind === 'audio') {
        // Register Music Audio track
        await this.soundControl
          .setAudioElementTrack(event.streams[0])
          .catch((e) => this.error('Error setAudioElement', e));
      }
    };

    peerConnection.onconnectionstatechange = () => {
      if (!this.peerConnection) {
        return;
      }
      const { connectionState } = this.peerConnection;
      this.info('Music peer Subscribe state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          this.peerConnectionTime = Date.now();

          this.sendRtcStats(connectionState).catch((e) => this.error('Error sending rtc stats for music', e));
          this.startRtcStats();
          // TODO: this is not getting cleaned up, does anybody check these stats?
          // this.videoRoomService.startPeerConnectionStats('PlaylistService', 1000);

          break;

        case 'failed':
          this.handleConnectionFailed();
          break;
        case 'disconnected':
          closeRtcPeer(this.peerConnection);
          break;

        default:
          break;
      }
    };

    try {
      this.trace('Create offer setRemoteDescription', offer);
      await peerConnection
        .setRemoteDescription(offer as RTCSessionDescriptionInit)
        .catch((e) => this.error('Error setRemoteDescription', e));

      this.trace('Create offer awaiting answer', offer);
      const desc = await peerConnection.createAnswer().catch((e) => this.error('Error createAnswer', e));

      this.trace('Create offer got local description', desc);
      if (desc) {
        await peerConnection.setLocalDescription(desc).catch((e) => this.error('Error setLocalDescription', e));
      }

      this.trace('Create offer set local description and send to peer');
      if (peerConnection.localDescription) {
        this.websocketService.sendMessage(peerConnection.localDescription, { force: true });
      }
    } catch (e) {
      this.error('Peer connection error: ', e);
    }
  }

  private async sendRtcStats(state: string): Promise<void> {
    if (!this.peerConnection || this.peerConnection.connectionState === 'closed') {
      return;
    }
    const stats = await this.peerConnection.getStats().catch((e) => this.error('Error getStats', e));
    if (stats) {
      const rtcReports: RTCStatsReport[] = [];
      stats.forEach((report) => rtcReports.push(report));
      this.info('Music RTC Stats ', { rtcReports, state });
    } else {
      this.info('Music No RTC stats', { state });
    }
  }

  /**
   * RTC Stats
   */
  private startRtcStats(): void {
    this.info('RTC Stats START for music');
    const rtcStats = (this.rtcStats = new ParticipantRtcStats(''));
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    this.rtcVideoStatsInterval = setInterval(async () => {
      // Validate if peer and videoTrack is still available
      let isAudioStuck = false;

      if (!this.peerConnection || this.peerConnection.connectionState === 'closed') {
        return;
      }

      const stats = await this.peerConnection.getStats().catch((e) => this.error('Error getStats', e));
      if (stats) {
        isAudioStuck = rtcStats.isAudioStuck(stats, 'music', 'music');
      }

      if (isAudioStuck && this.rtcVideoStatsInterval && this.messageSubscription) {
        this.warn('Music Media is Stuck RESUBSCRIBE', {
          isAudioStuck,
        });
        this.destroyRtcStats();
        this.handleConnectionFailed();
      }
    }, STATS_INTERVAL);
  }

  private destroyRtcStats(): void {
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
      this.rtcVideoStatsInterval = undefined;
    }
    if (this.rtcStats) {
      this.info('Music RTC Stats STOP');
    }
    this.rtcStats?.destroy();
    this.rtcVideoStatsInterval = undefined;
    this.rtcStats = undefined;
  }

  public destroy(): void {
    this.info('Destroying playlist service');

    this.destroyRtcStats();

    this.peerConnection?.close();
    this.peerConnection = undefined;
    this.peerConnectionTime = undefined;

    this.messageSubscription?.unsubscribe();
    this.messageSubscription = undefined;

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
